/**
 * Cart: re-price a customization fee whose stored currency is stale.
 *
 * `_fee_total` is stamped at add-to-cart time with the currency the shopper was
 * browsing in. Shopify re-prices the cart when the currency changes; a line
 * property cannot follow. Before this, cart-line-items.liquid dropped the fee
 * line entirely in that case, so the shopper saw her request with no price and
 * then a different number at checkout — NPL#48655 was shown 9,00 EUR and
 * charged 46,00 RON. Those are the same money (~10 USD); only the label was
 * wrong, which is worse than it sounds because her invoice contradicts her cart.
 *
 * The number here MUST match what the Cart Transform function will charge, so
 * this repeats its branch order exactly:
 *   1. the per-currency row from the config, when the currency has one;
 *   2. otherwise round(USD tier x Shopify.currency.rate) — the same rounding,
 *      on the same rate the storefront is showing.
 * Anything else would trade a wrong label for a wrong number.
 *
 * What this deliberately does NOT do: rewrite the line's properties. They are
 * what reaches the order, and rewriting them from the client would put a second
 * writer on the money path. The property stays as written; the shopper sees the
 * live figure and a line telling her why it moved.
 */
(function () {
  'use strict';

  var TIER_OF = { std_request: 'standard', adv_request: 'advanced' };

  function config() {
    var c = window.NPL_CUSTOMIZATION_CONFIG_METAFIELD;
    return c && c.pricing && c.pricing.tiers ? c : null;
  }

  /** One tier's price in `currency`, by the Cart Transform function's own rule. */
  function tierPrice(cfg, tier, currency) {
    var row = cfg.pricing.by_currency && cfg.pricing.by_currency[currency];
    if (row && typeof row[tier] === 'number') return row[tier];
    var usd = cfg.pricing.tiers[tier];
    if (typeof usd !== 'number') return null;
    var rate = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
      ? parseFloat(window.Shopify.currency.rate)
      : 0;
    if (!(rate > 0)) return null; // no rate = no honest number to show
    return Math.round(usd * rate);
  }

  function totalFor(cfg, breakdown, currency) {
    var sum = 0;
    for (var i = 0; i < breakdown.length; i++) {
      var tier = TIER_OF[breakdown[i] && breakdown[i].item];
      if (!tier) continue; // an entry we do not price is not one we may guess at
      var p = tierPrice(cfg, tier, currency);
      if (p === null) return null;
      sum += p;
    }
    return sum;
  }

  function money(units, currency) {
    return units.toFixed(2).replace('.', ',') + ' ' + currency;
  }

  function fill(el) {
    var cfg = config();
    if (!cfg) return;

    var breakdown;
    try {
      breakdown = JSON.parse(el.getAttribute('data-fee-breakdown') || '[]');
    } catch (e) {
      return;
    }
    if (!Array.isArray(breakdown) || breakdown.length === 0) return;

    var currency = el.getAttribute('data-fee-currency') || '';
    var total = totalFor(cfg, breakdown, currency);
    if (total === null) return; // leave it blank rather than show a guess

    var stored = (el.getAttribute('data-fee-stored') || '').split(' ');
    var storedCurrency = stored.length === 2 ? stored[1] : '';

    var html =
      '<strong>Customizations fee:</strong> ' + money(total, currency);
    if (storedCurrency && storedCurrency !== currency) {
      html +=
        '<div style="margin-top:4px;font-size:0.78rem;color:#8a8a8a;">' +
        'Updated to ' + currency + ' — this fee was added while prices were shown in ' +
        storedCurrency + '. This is the amount you will be charged.' +
        '</div>';
    }
    el.innerHTML = html;
  }

  function run() {
    var nodes = document.querySelectorAll('[data-fee-recalc]');
    for (var i = 0; i < nodes.length; i++) fill(nodes[i]);
  }

  document.addEventListener('DOMContentLoaded', run);
  if (document.readyState !== 'loading') run();
  // The cart re-renders its lines after a quantity change; re-fill then too.
  document.addEventListener('cart:updated', run);
  document.addEventListener('cart:refresh', run);
})();
